import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import LanguageToggle from '../components/LanguageToggle';
import { api } from '../lib/api';
import { Zap, Sparkles, ChevronLeft } from 'lucide-react';

const GOOGLE_AUTH_HOST = 'https://auth.emergentagent.com/';

export default function AuthPage() {
  const { t } = useI18n();
  const { loginWithPhone, register, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const roleReq = params.get('role'); // 'organizer' | null
  const initialMode = params.get('mode') === 'signin' ? 'signIn' : (roleReq === 'organizer' ? 'signUp' : 'signIn');
  const [mode, setMode] = useState(initialMode);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate(location.state?.from?.pathname || '/home', { replace: true });
  }, [user, navigate, location.state]);

  // Sign-in form
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');

  // Sign-up form
  const [su, setSu] = useState({
    name: '', username: '', phone: '', password: '',
    birthdate: '', country: '', state: '', city: '',
    accept_terms: false, accept_privacy: false, accept_community: false,
  });
  const [usernameStatus, setUsernameStatus] = useState(null); // null | 'checking' | 'ok' | 'taken'

  useEffect(() => {
    if (mode !== 'signUp') return;
    const u = (su.username || '').trim();
    if (u.length < 3) { setUsernameStatus(null); return; }
    setUsernameStatus('checking');
    const id = setTimeout(async () => {
      try {
        const { data } = await api.post('/auth/check-username', { username: u });
        setUsernameStatus(data.available ? 'ok' : 'taken');
      } catch (_e) { setUsernameStatus(null); }
    }, 400);
    return () => clearTimeout(id);
  }, [su.username, mode]);

  const canSubmitSignUp = useMemo(() => (
    su.name && su.username && su.phone && su.password && su.birthdate && su.country && su.state && su.city &&
    su.accept_terms && su.accept_privacy && su.accept_community && usernameStatus === 'ok'
  ), [su, usernameStatus]);

  const submitSignIn = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await loginWithPhone(phone, password);
      navigate('/home', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('auth.errorInvalid'));
    } finally { setBusy(false); }
  };

  const submitSignUp = async (e) => {
    e.preventDefault();
    if (!canSubmitSignUp) return;
    setBusy(true);
    try {
      await register({ ...su, requested_role: roleReq === 'organizer' ? 'organizer' : undefined });
      navigate('/home', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('common.error'));
    } finally { setBusy(false); }
  };

  const goGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/home';
    window.location.href = `${GOOGLE_AUTH_HOST}?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-aura-bg relative overflow-hidden">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-aura-cyan/20 blur-3xl" />
      <div className="absolute -bottom-32 -right-24 h-96 w-96 rounded-full bg-aura-violet/20 blur-3xl" />

      <div className="relative mx-auto max-w-md px-5 pt-6 pb-16">
        <header className="flex items-center justify-between mb-8">
          <Link to="/" data-testid="btn-home" className="flex items-center gap-2 hover:opacity-80 transition">
            <ChevronLeft size={18} className="text-aura-textDim" />
            <Zap className="text-aura-cyan drop-shadow-[0_0_12px_rgba(0,240,255,0.7)]" />
            <span className="font-display font-black text-lg tracking-[0.3em] aura-text-gradient">AURA</span>
          </Link>
          <LanguageToggle />
        </header>

        {roleReq === 'organizer' && (
          <div className="mb-6 aura-card p-4 border-aura-gold/40" data-testid="role-badge-organizer">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-aura-gold/10 border border-aura-gold/30 grid place-items-center">
                <Sparkles size={18} className="text-aura-gold" />
              </div>
              <div>
                <div className="font-display font-black text-aura-gold tracking-widest text-sm">REGISTRO ORGANIZADOR</div>
                <p className="text-xs text-aura-textDim mt-1">
                  Al registrarte obtienes acceso al Panel del Organizador y podrás crear eventos, registrar contendientes y declarar ganadores.
                </p>
              </div>
            </div>
          </div>
        )}

        <h1 data-testid="auth-title" className="font-display font-black text-4xl sm:text-5xl leading-[0.95] tracking-tight mb-2">
          <span className="aura-text-gradient">{roleReq === 'organizer' ? 'Sé un Organizador' : t('auth.title')}</span>
        </h1>
        <p className="text-aura-textDim mb-8 text-sm">
          {roleReq === 'organizer' ? 'Crea eventos AURA en tu ciudad. Registra contendientes por QR y declara ganadores en vivo.' : t('auth.subtitle')}
        </p>

        {/* Google button */}
        <button
          onClick={goGoogle}
          data-testid="google-signin-btn"
          className="w-full aura-btn-ghost h-12 font-semibold text-base"
        >
          <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.7 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.2 13.4 17.6 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.5 24.5c0-1.5-.2-3-.5-4.5H24v9h12.7c-.6 3-2.3 5.5-4.9 7.2l7.5 5.8c4.4-4 6.9-9.9 6.9-17.5z" />
            <path fill="#FBBC05" d="M10.4 28.6c-.5-1.4-.8-2.9-.8-4.6s.3-3.2.8-4.6l-7.8-6.1C.9 16.8 0 20.3 0 24s.9 7.2 2.6 10.6l7.8-6z" />
            <path fill="#34A853" d="M24 48c6.3 0 11.5-2.1 15.4-5.7l-7.5-5.8c-2.1 1.4-4.8 2.2-7.9 2.2-6.4 0-11.8-3.9-13.6-9.4l-7.8 6C6.5 42.6 14.6 48 24 48z" />
          </svg>
          {t('auth.signInWithGoogle')}
        </button>

        <div className="flex items-center gap-3 my-6">
          <div className="h-px flex-1 bg-white/10" />
          <span className="text-xs text-aura-textDim uppercase tracking-widest">{t('auth.orDivider')}</span>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        {mode === 'signIn' ? (
          <form onSubmit={submitSignIn} className="space-y-4">
            <Field label={t('auth.phone')} hint={t('auth.phoneHint')}>
              <input data-testid="signin-phone" required className="aura-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+52 55 0000 0000" />
            </Field>
            <Field label={t('auth.password')} hint={t('auth.passwordHint')}>
              <input data-testid="signin-password" required type="password" className="aura-input" value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
            <button data-testid="signin-submit" disabled={busy} className="aura-btn-primary w-full h-12 text-base disabled:opacity-60">
              {busy ? t('common.loading') : t('auth.submitSignIn')}
            </button>
            <button type="button" onClick={() => setMode('signUp')} data-testid="switch-to-signup" className="w-full text-sm text-aura-textDim hover:text-white transition">
              {t('auth.switchToSignUp')}
            </button>
          </form>
        ) : (
          <form onSubmit={submitSignUp} className="space-y-4" data-testid="signup-form">
            <Field label={t('auth.name')}>
              <input data-testid="signup-name" required className="aura-input" value={su.name} onChange={(e) => setSu({ ...su, name: e.target.value })} maxLength={80} />
            </Field>
            <Field
              label={t('auth.username')}
              hint={t('auth.usernameHint')}
              status={usernameStatus === 'checking' ? t('auth.usernameChecking') : usernameStatus === 'ok' ? t('auth.usernameAvailable') : usernameStatus === 'taken' ? t('auth.usernameTaken') : null}
              statusOk={usernameStatus === 'ok'}
              statusBad={usernameStatus === 'taken'}
            >
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-aura-textDim">@</span>
                <input data-testid="signup-username" required className="aura-input pl-7" value={su.username} onChange={(e) => setSu({ ...su, username: e.target.value.replace(/[^a-zA-Z0-9_]/g, '') })} maxLength={24} />
              </div>
            </Field>
            <Field label={t('auth.phone')} hint={t('auth.phoneHint')}>
              <input data-testid="signup-phone" required className="aura-input" value={su.phone} onChange={(e) => setSu({ ...su, phone: e.target.value })} placeholder="+52 55 0000 0000" />
            </Field>
            <Field label={t('auth.password')} hint={t('auth.passwordHint')}>
              <input data-testid="signup-password" required type="password" className="aura-input" value={su.password} onChange={(e) => setSu({ ...su, password: e.target.value })} minLength={8} />
            </Field>
            <Field label={t('auth.birthdate')}>
              <input data-testid="signup-birthdate" required type="date" className="aura-input [color-scheme:dark]" value={su.birthdate} onChange={(e) => setSu({ ...su, birthdate: e.target.value })} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('auth.country')}>
                <input data-testid="signup-country" required className="aura-input" value={su.country} onChange={(e) => setSu({ ...su, country: e.target.value })} placeholder="MX" />
              </Field>
              <Field label={t('auth.state')}>
                <input data-testid="signup-state" required className="aura-input" value={su.state} onChange={(e) => setSu({ ...su, state: e.target.value })} placeholder="CDMX" />
              </Field>
            </div>
            <Field label={t('auth.city')}>
              <input data-testid="signup-city" required className="aura-input" value={su.city} onChange={(e) => setSu({ ...su, city: e.target.value })} placeholder="Mexico City" />
            </Field>

            <div className="space-y-2 pt-2">
              <LegalCheck testId="accept-terms" checked={su.accept_terms} onChange={(v) => setSu({ ...su, accept_terms: v })} label={t('auth.acceptTerms')} />
              <LegalCheck testId="accept-privacy" checked={su.accept_privacy} onChange={(v) => setSu({ ...su, accept_privacy: v })} label={t('auth.acceptPrivacy')} />
              <LegalCheck testId="accept-community" checked={su.accept_community} onChange={(v) => setSu({ ...su, accept_community: v })} label={t('auth.acceptCommunity')} />
            </div>

            <button data-testid="signup-submit" disabled={busy || !canSubmitSignUp} className="aura-btn-primary w-full h-12 text-base disabled:opacity-50">
              {busy ? t('common.loading') : t('auth.submitSignUp')}
            </button>
            <button type="button" onClick={() => setMode('signIn')} data-testid="switch-to-signin" className="w-full text-sm text-aura-textDim hover:text-white transition">
              {t('auth.switchToSignIn')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, hint, status, statusOk, statusBad, children }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] uppercase tracking-widest font-display font-semibold text-aura-textDim">{label}</span>
        {status && <span className={`text-[11px] font-medium ${statusOk ? 'text-aura-cyan' : statusBad ? 'text-red-400' : 'text-aura-textDim'}`}>{status}</span>}
      </div>
      {children}
      {hint && <div className="text-[11px] text-white/40 mt-1">{hint}</div>}
    </label>
  );
}

function LegalCheck({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-start gap-2 text-sm text-white/80 cursor-pointer">
      <input
        type="checkbox"
        data-testid={testId}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-white/20 bg-white/10 accent-aura-cyan"
      />
      <span>{label}</span>
    </label>
  );
}
